import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, ChevronLeft, CalendarClock, Copy, Play, RefreshCw, Search, Users } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { fmtTime12, isoDate } from '../../utils/time';

const REC_TYPES = [
  { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'three_weekly', label: 'Every 3 weeks' }, { value: 'four_weekly', label: 'Every 4 weeks' }, { value: 'monthly', label: 'Monthly' },
];
const DOWS = [['dowMon', 'Mon'], ['dowTue', 'Tue'], ['dowWed', 'Wed'], ['dowThu', 'Thu'], ['dowFri', 'Fri'], ['dowSat', 'Sat'], ['dowSun', 'Sun']];
const recLabel = (v) => REC_TYPES.find(r => r.value === v)?.label || v;

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[88vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, required, children, hint }) {
  return <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>{children}{hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}</div>;
}

// ── Item editor modal ────────────────────────────────────────────────────────
const EMPTY_ITEM = {
  serviceTypeId: '', fundId: '', startTime: '09:00', endTime: '12:00', breakMinutes: 0, nextDay: false,
  employeeId: '', recurringType: 'weekly', dowMon: false, dowTue: false, dowWed: false, dowThu: false, dowFri: false, dowSat: false, dowSun: false,
  dayOfMonth: '', activity: '', rosterComments: '', tasks: [], clients: [],
};
function ItemModal({ template, item, serviceTypes, staff, funds, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [taskInput, setTaskInput] = useState('');
  const [saving, setSaving] = useState(false);
  const isGroup = template.template_type === 'group';
  // A group line runs for several participants. The API has always stored them
  // (service_template_item_clients) and generation books each one, but the editor had no way to
  // pick them, so a group template could only ever produce shifts with nobody on them.
  const [clientList, setClientList] = useState([]);
  const [pick, setPick] = useState('');
  useEffect(() => {
    if (!isGroup) return;
    api.get('/clients?limit=500').then(r => setClientList((r.data.clients || r.data || []).filter(c => c.is_active !== false))).catch(() => {});
  }, [isGroup]);
  useEffect(() => {
    if (item) setForm({
      serviceTypeId: item.service_type_id || '', fundId: item.fund_id || '', startTime: (item.start_time || '09:00').slice(0, 5),
      endTime: (item.end_time || '12:00').slice(0, 5), breakMinutes: item.break_minutes || 0, nextDay: !!item.next_day,
      employeeId: item.employee_id || '', recurringType: item.recurring_type || 'weekly',
      dowMon: !!item.dow_mon, dowTue: !!item.dow_tue, dowWed: !!item.dow_wed, dowThu: !!item.dow_thu, dowFri: !!item.dow_fri, dowSat: !!item.dow_sat, dowSun: !!item.dow_sun,
      dayOfMonth: item.day_of_month || '', activity: item.activity || '', rosterComments: item.roster_comments || '',
      tasks: (item.tasks || []).map(t => ({ title: t.title, is_mandatory: t.is_mandatory })),
      clients: (item.clients || []).map(c => ({ clientId: c.client_id, fundId: c.fund_id || null, name: c.client_name })),
    });
    else setForm(EMPTY_ITEM);
  }, [item]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isMonthly = form.recurringType === 'monthly';
  const isDaily = form.recurringType === 'daily';

  const save = async () => {
    if (!form.startTime || !form.endTime) return toast.error('Start and end time required');
    if (!isMonthly && !isDaily && !DOWS.some(([k]) => form[k])) return toast.error('Select at least one day of the week');
    if (isGroup && !form.clients.length) return toast.error('Add at least one participant');
    setSaving(true);
    try {
      const { clients, ...rest } = form;
      const body = isGroup ? { ...rest, clients: clients.map(c => ({ clientId: c.clientId, fundId: c.fundId })) } : rest;
      if (item) await api.put(`/templates/${template.id}/items/${item.id}`, body);
      else await api.post(`/templates/${template.id}/items`, body);
      toast.success(item ? 'Item updated' : 'Item added'); onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Modal wide title={item ? 'Edit Service Line' : 'Add Service Line'} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Service Type" required>
          <select className="input-field" value={form.serviceTypeId} onChange={e => set('serviceTypeId', e.target.value)}>
            <option value="">Select…</option>
            {serviceTypes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        {template.template_type !== 'group' && (
          <Field label="Funding Source" hint="Blank = client's default fund">
            <select className="input-field" value={form.fundId} onChange={e => set('fundId', e.target.value)}>
              <option value="">Default</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.fund_name || f.name || f.fund_type || 'Fund'}</option>)}
            </select>
          </Field>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Start Time" required><input type="time" className="input-field" value={form.startTime} onChange={e => set('startTime', e.target.value)} /></Field>
        <Field label="End Time" required><input type="time" className="input-field" value={form.endTime} onChange={e => set('endTime', e.target.value)} /></Field>
        <Field label="Break (min)"><input type="number" min="0" className="input-field" value={form.breakMinutes} onChange={e => set('breakMinutes', e.target.value)} /></Field>
      </div>
      {isGroup && (
        <Field label="Participants" required hint="Everyone who attends this session. Each gets their own booking, charged to their default fund.">
          <div className="flex gap-2">
            <select className="input-field flex-1" value={pick} onChange={e => setPick(e.target.value)} aria-label="Add a participant">
              <option value="">Add a participant…</option>
              {clientList.filter(c => !form.clients.some(x => x.clientId === c.id)).map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
            <button type="button" className="btn-secondary btn-sm" disabled={!pick} onClick={() => {
              const c = clientList.find(x => x.id === pick);
              if (c) set('clients', [...form.clients, { clientId: c.id, fundId: null, name: `${c.first_name} ${c.last_name}` }]);
              setPick('');
            }}>Add</button>
          </div>
          {form.clients.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {form.clients.map(c => (
                <span key={c.clientId} className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-800 px-2.5 py-1 text-xs font-medium">
                  {c.name || 'Participant'}
                  <button type="button" onClick={() => set('clients', form.clients.filter(x => x.clientId !== c.clientId))} className="text-violet-500 hover:text-red-600" aria-label={`Remove ${c.name || 'participant'}`}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </Field>
      )}
      <Field label="Assigned Worker" hint="Optional — leave blank to roster later">
        <select className="input-field" value={form.employeeId} onChange={e => set('employeeId', e.target.value)}>
          <option value="">Unassigned</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
        </select>
      </Field>

      <Field label="Recurrence">
        <select className="input-field" value={form.recurringType} onChange={e => set('recurringType', e.target.value)}>
          {REC_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </Field>
      {isMonthly ? (
        <Field label="Day of month" hint="1–31 (clamps to month length)"><input type="number" min="1" max="31" className="input-field w-32" value={form.dayOfMonth} onChange={e => set('dayOfMonth', e.target.value)} placeholder="e.g. 15" /></Field>
      ) : (
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Days {isDaily && <span className="text-slate-400">(optional for Daily)</span>}</label>
          <div className="flex flex-wrap gap-1.5">
            {DOWS.map(([k, l]) => (
              <button key={k} type="button" onClick={() => set(k, !form[k])}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${form[k] ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>{l}</button>
            ))}
          </div>
        </div>
      )}

      <Field label="Activity"><input className="input-field" value={form.activity} onChange={e => set('activity', e.target.value)} placeholder="Shift narrative shown to worker" /></Field>
      <Field label="Roster Comments"><textarea className="input-field" rows={2} value={form.rosterComments} onChange={e => set('rosterComments', e.target.value)} /></Field>

      <Field label="Tasks">
        <div className="flex gap-2">
          <input className="input-field flex-1" placeholder="Add a task…" value={taskInput} onChange={e => setTaskInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (taskInput.trim()) { set('tasks', [...form.tasks, { title: taskInput.trim(), is_mandatory: false }]); setTaskInput(''); } } }} />
          <button type="button" className="btn-secondary btn-sm" onClick={() => { if (taskInput.trim()) { set('tasks', [...form.tasks, { title: taskInput.trim(), is_mandatory: false }]); setTaskInput(''); } }}>Add</button>
        </div>
        <div className="space-y-1 mt-2">
          {form.tasks.map((t, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <label className="flex items-center gap-1 text-xs text-slate-500"><input type="checkbox" checked={t.is_mandatory} onChange={e => set('tasks', form.tasks.map((x, j) => j === i ? { ...x, is_mandatory: e.target.checked } : x))} /> mandatory</label>
              <span className="flex-1 text-slate-700">{t.title}</span>
              <button onClick={() => set('tasks', form.tasks.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
            </div>
          ))}
        </div>
      </Field>

      <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
        <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
        <button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving…' : item ? 'Save' : 'Add'}</button>
      </div>
    </Modal>
  );
}

// ── Generate modal ───────────────────────────────────────────────────────────
function GenerateModal({ template, onClose, onDone }) {
  const [fromDate, setFrom] = useState('');
  const [toDate, setTo] = useState('');
  const [skipConflicts, setSkip] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dryRun) => {
    if (!fromDate || !toDate) return toast.error('Pick a date range');
    if (toDate < fromDate) return toast.error('End date must be after start');
    setBusy(true);
    try {
      const { data } = await api.post(`/templates/${template.id}/generate`, { fromDate, toDate, skipConflicts, dryRun });
      if (dryRun) { setPreview(data); }
      else { toast.success(`Created ${data.created} shift${data.created !== 1 ? 's' : ''}${data.skipped ? ` (${data.skipped} skipped)` : ''}`); onDone(); }
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Create Service Schedule" onClose={onClose}>
      <p className="text-sm text-slate-500">Generate shifts from this template's active service lines across a date range.</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From" required><input type="date" className="input-field" value={fromDate} onChange={e => { setFrom(e.target.value); setPreview(null); }} /></Field>
        <Field label="To" required><input type="date" className="input-field" value={toDate} onChange={e => { setTo(e.target.value); setPreview(null); }} /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={skipConflicts} onChange={e => setSkip(e.target.checked)} /> Skip shifts that clash with the assigned worker</label>
      {preview && <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">Preview: <b>{preview.created}</b> shift(s) would be created{preview.skipped ? `, ${preview.skipped} skipped (already exist)` : ''}.</div>}
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
        <button onClick={() => run(true)} disabled={busy} className="btn-secondary btn-sm">Preview</button>
        <button onClick={() => run(false)} disabled={busy} className="btn-primary btn-sm flex items-center gap-1.5"><Play size={13} /> Generate</button>
      </div>
    </Modal>
  );
}

// ── Update-generated modal ───────────────────────────────────────────────────
const UPD_FIELDS = [
  { key: 'times', label: 'Times (start/end)', fields: ['start_time', 'end_time'] },
  { key: 'staff', label: 'Assigned worker', fields: ['staff_id'] },
  { key: 'break', label: 'Break', fields: ['break_minutes'] },
  { key: 'address', label: 'Address', fields: ['address'] },
  { key: 'service', label: 'Service type', fields: ['service_type_id'] },
  { key: 'narrative', label: 'Activity & comments', fields: ['activity', 'roster_comments'] },
];
function UpdateModal({ template, onClose, onDone }) {
  const [sel, setSel] = useState({});
  const [fromDate, setFrom] = useState(isoDate());
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    const fields = UPD_FIELDS.filter(f => sel[f.key]).flatMap(f => f.fields);
    if (!fields.length) return toast.error('Select at least one field to update');
    setBusy(true);
    try {
      const { data } = await api.post(`/templates/${template.id}/update-generated`, { fields, fromDate });
      toast.success(`Updated ${data.updated} future shift${data.updated !== 1 ? 's' : ''}`); onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Update Service Schedule" onClose={onClose}>
      <p className="text-sm text-slate-500">Push your template-line edits to <b>future, un-completed</b> generated shifts only. Past and completed/cancelled shifts are never touched.</p>
      <Field label="Apply from date"><input type="date" className="input-field w-44" value={fromDate} onChange={e => setFrom(e.target.value)} /></Field>
      <div className="space-y-1.5">
        {UPD_FIELDS.map(f => (
          <label key={f.key} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!sel[f.key]} onChange={e => setSel(s => ({ ...s, [f.key]: e.target.checked }))} /> {f.label}</label>
        ))}
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
        <button onClick={apply} disabled={busy} className="btn-primary btn-sm">{busy ? 'Updating…' : 'Apply'}</button>
      </div>
    </Modal>
  );
}

// ── Editor ───────────────────────────────────────────────────────────────────
function TemplateEditor({ id, onBack }) {
  const confirm = useConfirm();
  const [tpl, setTpl] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [funds, setFunds] = useState([]);
  const [itemModal, setItemModal] = useState(null); // {item?} or null
  const [genModal, setGenModal] = useState(false);
  const [updModal, setUpdModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/templates/${id}`);
      setTpl(data);
      if (data.client_id) api.get(`/clients/${data.client_id}`).then(r => setFunds(r.data.funds || [])).catch(() => {});
    } catch { toast.error('Failed to load template'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/service-types?active=true').then(r => setServiceTypes(r.data || [])).catch(() => {});
    api.get('/staff').then(r => setStaff((r.data || []).filter(s => s.is_active !== false))).catch(() => {});
  }, []);

  const delItem = async (it) => {
    if (!await confirm('Delete this service line?', { title: 'Delete Line', danger: true })) return;
    try { await api.delete(`/templates/${id}/items/${it.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const dupItem = async (it) => {
    try { await api.post(`/templates/${id}/items/${it.id}/copy`); toast.success('Line duplicated'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const copyTemplate = async () => {
    try { const { data } = await api.post(`/templates/${id}/copy`); toast.success('Template copied'); onBack(data.id); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  if (!tpl) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="w-full space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => onBack()} className="btn-ghost btn-sm p-2"><ChevronLeft size={16} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="page-title truncate">{tpl.name}</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            <span className={`px-2 py-0.5 rounded text-xs font-medium mr-2 ${tpl.template_type === 'group' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'}`}>{tpl.template_type}</span>
            {tpl.client_name || '—'}
          </p>
        </div>
        <button onClick={() => setGenModal(true)} className="btn-primary flex items-center gap-1.5"><Play size={14} /> Create Service Schedule</button>
        <button onClick={() => setUpdModal(true)} className="btn-secondary flex items-center gap-1.5"><RefreshCw size={14} /> Update Generated</button>
        <button onClick={copyTemplate} className="btn-secondary flex items-center gap-1.5"><Copy size={14} /> Copy</button>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Service Lines</h2>
        <button onClick={() => setItemModal({})} className="btn-primary btn-sm flex items-center gap-1.5"><Plus size={13} /> Add Line</button>
      </div>

      {(tpl.items || []).length === 0 ? (
        <div className="card p-10 text-center text-slate-400 text-sm">No service lines yet — add one to define the recurring pattern.</div>
      ) : (
        <div className="grid gap-3">
          {tpl.items.map(it => (
            <div key={it.id} className="card p-4 flex items-center gap-4">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: it.service_type_color || '#6366f1' }} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">{it.service_type_name || 'Service'}</p>
                <p className="text-xs text-slate-500">
                  {fmtTime12(it.start_time)}–{fmtTime12(it.end_time)} · {recLabel(it.recurring_type)}
                  {it.recurring_type !== 'monthly' && it.recurring_type !== 'daily' && ` · ${DOWS.filter(([k]) => it[k.replace('dow', 'dow_').toLowerCase().replace('dow_', 'dow_')]).map(([, l]) => l).join('/')}`}
                  {it.recurring_type === 'monthly' && it.day_of_month && ` · day ${it.day_of_month}`}
                  {it.employee_id ? '' : ' · unassigned'}
                  {it.tasks?.length ? ` · ${it.tasks.length} task(s)` : ''}
                </p>
              </div>
              {tpl.template_type === 'group' && <span className="text-xs text-slate-400 flex items-center gap-1"><Users size={12} />{it.clients?.length || 0}</span>}
              <button onClick={() => setItemModal({ item: it })} className="p-1.5 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded"><Pencil size={13} /></button>
              <button onClick={() => dupItem(it)} className="p-1.5 text-slate-400 hover:text-primary-500 hover:bg-primary-50 rounded"><Copy size={13} /></button>
              <button onClick={() => delItem(it)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {itemModal && <ItemModal template={tpl} item={itemModal.item} serviceTypes={serviceTypes} staff={staff} funds={funds} onClose={() => setItemModal(null)} onSaved={() => { setItemModal(null); load(); }} />}
      {genModal && <GenerateModal template={tpl} onClose={() => setGenModal(false)} onDone={() => setGenModal(false)} />}
      {updModal && <UpdateModal template={tpl} onClose={() => setUpdModal(false)} onDone={() => setUpdModal(false)} />}
    </div>
  );
}

// ── List ─────────────────────────────────────────────────────────────────────
function TemplateList({ onOpen }) {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: '', templateType: 'individual', clientId: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/templates'); setList(Array.isArray(data) ? data : []); }
    catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/clients?limit=500').then(r => setClients(r.data.clients || r.data || [])).catch(() => {}); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    if (form.templateType === 'individual' && !form.clientId) return toast.error('Select a client');
    setSaving(true);
    try { const { data } = await api.post('/templates', form); toast.success('Template created'); setModal(false); onOpen(data.id); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };
  const del = async (t) => {
    if (!await confirm(`Delete "${t.name}"? Generated shifts are kept.`, { title: 'Delete Template', danger: true })) return;
    try { const { data } = await api.delete(`/templates/${t.id}`); toast.success(data.message || 'Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const filtered = list.filter(t => !search.trim() || t.name?.toLowerCase().includes(search.toLowerCase()) || t.client_name?.toLowerCase().includes(search.toLowerCase()));
  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="w-full space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">Service Templates</h1>
          <p className="text-sm text-slate-400 mt-0.5">Reusable recurring rosters — generate weeks of shifts in one click.</p>
        </div>
        <button onClick={() => { setForm({ name: '', templateType: 'individual', clientId: '' }); setModal(true); }} className="btn-primary flex items-center gap-1.5"><Plus size={14} /> New Template</button>
      </div>
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="input-field pl-8 text-sm" placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 ? (
        <div className="card p-12 text-center">
          <CalendarClock size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">{list.length === 0 ? 'No templates yet' : 'No matches'}</p>
          <p className="text-sm text-slate-400 mt-1">Create a template to define a client's recurring service pattern.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(t => (
            <div key={t.id} className="card p-5 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => onOpen(t.id)}>
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center shrink-0"><CalendarClock size={18} className="text-primary-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800">{t.name} {!t.is_active && <span className="text-xs text-slate-400">(inactive)</span>}</p>
                <p className="text-sm text-slate-400">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium mr-2 ${t.template_type === 'group' ? 'bg-violet-50 text-violet-700' : 'bg-blue-50 text-blue-700'}`}>{t.template_type}</span>
                  {t.client_name || '—'} · {t.item_count || 0} line(s)
                </p>
              </div>
              <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                <button onClick={() => onOpen(t.id)} className="btn-secondary btn-sm">Open</button>
                <button onClick={() => del(t)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title="New Service Template" onClose={() => setModal(false)}>
          <Field label="Template Name" required><input className="input-field" placeholder="e.g. Weekly Supports — Jane D" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
          <Field label="Type"><select className="input-field" value={form.templateType} onChange={e => setForm(f => ({ ...f, templateType: e.target.value }))}><option value="individual">Individual</option><option value="group">Group</option></select></Field>
          {form.templateType === 'individual' && (
            <Field label="Client" required>
              <select className="input-field" value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
                <option value="">Select…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </Field>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setModal(false)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={create} disabled={saving} className="btn-primary btn-sm">{saving ? 'Creating…' : 'Create & Edit'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function Templates() {
  const [selectedId, setSelectedId] = useState(null);
  if (selectedId) return <TemplateEditor id={selectedId} onBack={(newId) => setSelectedId(newId || null)} />;
  return <TemplateList onOpen={setSelectedId} />;
}
